import React, { useEffect, useRef, useState } from "react";

export const LEGACY_RICH_POST_CONTENT_FORMAT = "rich_text_v1";
export const RICH_POST_CONTENT_FORMAT = "rich_text_v2";
export const MAX_POST_TEXT_LENGTH = 10000;
export const POST_EMOJIS = ["✅", "⭐", "❓", "👍", "👏", "🎉", "💡", "📌", "😊", "❤️"] as const;
export type PostEmoji = typeof POST_EMOJIS[number];

export const POST_TEXT_COLOURS = {
  red: { label: "Red", hex: "#b91c1c", className: "text-red-700" },
  blue: { label: "Blue", hex: "#1d4ed8", className: "text-blue-700" },
  green: { label: "Green", hex: "#15803d", className: "text-green-700" },
  purple: { label: "Purple", hex: "#7e22ce", className: "text-purple-700" },
} as const;

export type RichPostColour = keyof typeof POST_TEXT_COLOURS;
export type RichPostRun = { text: string; bold?: boolean; italic?: boolean; color?: RichPostColour };
export type RichPostDocument = { version: 1; runs: RichPostRun[] };

function isPostTextColour(value: unknown): value is RichPostColour {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(POST_TEXT_COLOURS, value);
}

export function plainTextDocument(text = ""): RichPostDocument {
  return { version: 1, runs: text ? [{ text }] : [] };
}

export function postPlainText(document: RichPostDocument): string {
  return document.runs.map((run) => run.text).join("");
}

export function parseRichPostDocument(value: string | null | undefined): RichPostDocument | null {
  try {
    const parsed = JSON.parse(value || "");
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.runs)) return null;
    const parsedRuns: unknown[] = parsed.runs;
    const runs = parsedRuns
      .filter((run: unknown): run is Record<string, unknown> => Boolean(run) && typeof run === "object")
      .map((run: Record<string, unknown>) => ({
        text: typeof run.text === "string" ? run.text : "",
        ...(run.bold === true ? { bold: true } : {}),
        ...(run.italic === true ? { italic: true } : {}),
        ...(isPostTextColour(run.color) ? { color: run.color } : {}),
      }))
      .filter((run: RichPostRun) => run.text.length > 0);
    return { version: 1, runs };
  } catch {
    return null;
  }
}

export function serialiseRichPostDocument(document: RichPostDocument): string {
  return JSON.stringify({
    version: 1,
    runs: document.runs
      .filter((run) => run.text.length > 0)
      .map((run) => ({ text: run.text, ...(run.bold ? { bold: true } : {}), ...(run.italic ? { italic: true } : {}), ...(run.color ? { color: run.color } : {}) })),
  });
}

function appendRun(runs: RichPostRun[], text: string, bold: boolean, italic: boolean, color?: RichPostColour) {
  if (!text) return;
  const previous = runs[runs.length - 1];
  if (previous && Boolean(previous.bold) === bold && Boolean(previous.italic) === italic && previous.color === color) {
    previous.text += text;
    return;
  }
  runs.push({ text, ...(bold ? { bold: true } : {}), ...(italic ? { italic: true } : {}), ...(color ? { color } : {}) });
}

function colourFromCss(value: string | null | undefined): RichPostColour | undefined {
  const compact = (value || "").replace(/\s+/g, "").toLowerCase();
  if (!compact) return undefined;
  for (const [colour, value] of Object.entries(POST_TEXT_COLOURS) as Array<[RichPostColour, typeof POST_TEXT_COLOURS[RichPostColour]]>) {
    const hex = value.hex.toLowerCase();
    const rgb = `rgb(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)})`;
    if (compact === hex || compact === rgb.replace(/\s+/g, "")) return colour;
  }
  return undefined;
}

function documentFromEditor(root: HTMLElement): RichPostDocument {
  const runs: RichPostRun[] = [];
  const walk = (node: Node, bold: boolean, italic: boolean, color?: RichPostColour) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendRun(runs, node.textContent || "", bold, italic, color);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    if (tag === "script" || tag === "style") return;
    if (tag === "br") {
      appendRun(runs, "\n", bold, italic, color);
      return;
    }
    const nextBold = bold || tag === "b" || tag === "strong";
    const nextItalic = italic || tag === "i" || tag === "em";
    const nextColour = colourFromCss(element.style.color || element.getAttribute("color")) || color;
    const block = tag === "div" || tag === "p";
    if (block && runs.length && !postPlainText({ version: 1, runs }).endsWith("\n")) appendRun(runs, "\n", bold, italic, color);
    Array.from(node.childNodes).forEach((child) => walk(child, nextBold, nextItalic, nextColour));
    if (block && !postPlainText({ version: 1, runs }).endsWith("\n")) appendRun(runs, "\n", bold, italic, color);
  };
  Array.from(root.childNodes).forEach((node) => walk(node, false, false));
  return { version: 1, runs };
}

function setEditorContents(root: HTMLElement, document: RichPostDocument) {
  root.replaceChildren();
  document.runs.forEach((run) => {
    const span = window.document.createElement("span");
    if (run.bold) span.style.fontWeight = "700";
    if (run.italic) span.style.fontStyle = "italic";
    if (run.color) span.style.color = POST_TEXT_COLOURS[run.color].hex;
    span.textContent = run.text;
    root.appendChild(span);
  });
}

export function PostBody({ content, contentFormat, className = "" }: { content: string; contentFormat?: string | null; className?: string }) {
  const rich = (contentFormat === RICH_POST_CONTENT_FORMAT || contentFormat === LEGACY_RICH_POST_CONTENT_FORMAT) ? parseRichPostDocument(content) : null;
  if (!rich) return <div className={`whitespace-pre-wrap ${className}`}>{content}</div>;
  return (
    <div className={`whitespace-pre-wrap ${className}`}>
      {rich.runs.map((run, index) => {
        let node: React.ReactNode = run.text;
        if (run.italic) node = <em>{node}</em>;
        if (run.bold) node = <strong>{node}</strong>;
        if (run.color) node = <span className={POST_TEXT_COLOURS[run.color].className}>{node}</span>;
        return <React.Fragment key={`${index}-${run.text}`}>{node}</React.Fragment>;
      })}
    </div>
  );
}

export function PostRichTextEditor({
  value,
  onChange,
  placeholder,
  boldLabel,
  italicLabel,
  colourLabel = "Text colour",
  defaultColourLabel = "Black (default)",
  emojiLabel = "Insert emoji",
  colourNames,
  emojiNames,
}: {
  value: RichPostDocument;
  onChange: (value: RichPostDocument) => void;
  placeholder: string;
  boldLabel: string;
  italicLabel: string;
  colourLabel?: string;
  defaultColourLabel?: string;
  emojiLabel?: string;
  colourNames?: Partial<Record<RichPostColour, string>>;
  emojiNames?: Partial<Record<PostEmoji, string>>;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<{ bold: boolean; italic: boolean; color?: RichPostColour }>({ bold: false, italic: false });
  const [colourOpen, setColourOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const lastValue = useRef("");

  useEffect(() => {
    const root = editorRef.current;
    const next = serialiseRichPostDocument(value);
    if (!root || next === lastValue.current) return;
    setEditorContents(root, value);
    lastValue.current = next;
  }, [value]);

  const refreshActive = () => {
    setActive({
      bold: Boolean(document.queryCommandState?.("bold")),
      italic: Boolean(document.queryCommandState?.("italic")),
      color: colourFromCss(document.queryCommandValue?.("foreColor")),
    });
  };

  const reportChange = () => {
    const root = editorRef.current;
    if (!root) return;
    const next = documentFromEditor(root);
    lastValue.current = serialiseRichPostDocument(next);
    onChange(next);
    refreshActive();
  };

  const apply = (command: "bold" | "italic") => {
    editorRef.current?.focus();
    document.execCommand(command);
    reportChange();
  };

  const applyColour = (colour?: RichPostColour) => {
    editorRef.current?.focus();
    document.execCommand("foreColor", false, colour ? POST_TEXT_COLOURS[colour].hex : "#0f172a");
    setColourOpen(false);
    reportChange();
  };

  const insertEmoji = (emoji: string) => {
    editorRef.current?.focus();
    document.execCommand("insertText", false, emoji);
    setEmojiOpen(false);
    reportChange();
  };

  return (
    <div className="overflow-hidden rounded-2xl border-2 border-slate-200 bg-white focus-within:ring-2 focus-within:ring-emerald-200">
      <div className="relative flex flex-wrap gap-2 border-b border-slate-200 bg-slate-50 p-2" role="toolbar" aria-label="Text formatting" onKeyDown={(event) => { if (event.key === "Escape") { setColourOpen(false); setEmojiOpen(false); editorRef.current?.focus(); } }}>
        <button type="button" aria-label={boldLabel} aria-pressed={active.bold} onMouseDown={(event) => event.preventDefault()} onClick={() => apply("bold")} className={`min-h-9 min-w-9 rounded-lg border px-2 font-black ${active.bold ? "border-emerald-500 bg-emerald-100 text-emerald-900" : "border-slate-200 bg-white text-slate-700"}`}><strong>B</strong></button>
        <button type="button" aria-label={italicLabel} aria-pressed={active.italic} onMouseDown={(event) => event.preventDefault()} onClick={() => apply("italic")} className={`min-h-9 min-w-9 rounded-lg border px-2 font-black italic ${active.italic ? "border-emerald-500 bg-emerald-100 text-emerald-900" : "border-slate-200 bg-white text-slate-700"}`}>I</button>
        <div className="static">
          <button type="button" aria-label={colourLabel} aria-haspopup="menu" aria-expanded={colourOpen} onMouseDown={(event) => event.preventDefault()} onClick={() => { setColourOpen((open) => !open); setEmojiOpen(false); }} className={`min-h-9 rounded-lg border px-2 text-xs font-black ${active.color ? "border-emerald-500 bg-emerald-100 text-emerald-900" : "border-slate-200 bg-white text-slate-700"}`}><span className={active.color ? POST_TEXT_COLOURS[active.color].className : "text-slate-900"}>A</span></button>
          {colourOpen && <div role="menu" aria-label={colourLabel} className="absolute right-2 top-full z-20 mt-1 grid w-[212px] max-w-[calc(100vw-1rem)] grid-cols-5 gap-1 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
            <button type="button" role="menuitem" aria-label={defaultColourLabel} aria-pressed={!active.color} onMouseDown={(event) => event.preventDefault()} onClick={() => applyColour()} className="min-h-9 min-w-9 rounded-lg border-2 border-slate-300 bg-slate-950 text-xs font-black text-white">A</button>
            {(Object.keys(POST_TEXT_COLOURS) as RichPostColour[]).map((colour) => <button key={colour} type="button" role="menuitem" aria-label={colourNames?.[colour] || POST_TEXT_COLOURS[colour].label} aria-pressed={active.color === colour} onMouseDown={(event) => event.preventDefault()} onClick={() => applyColour(colour)} className={`min-h-9 min-w-9 rounded-lg border-2 ${active.color === colour ? "border-emerald-500 ring-2 ring-emerald-200" : "border-transparent"}`}><span className={`text-lg ${POST_TEXT_COLOURS[colour].className}`}>A</span></button>)}
          </div>}
        </div>
        <div className="static">
          <button type="button" aria-label={emojiLabel} aria-haspopup="menu" aria-expanded={emojiOpen} onMouseDown={(event) => event.preventDefault()} onClick={() => { setEmojiOpen((open) => !open); setColourOpen(false); }} className="min-h-9 min-w-9 rounded-lg border border-slate-200 bg-white px-2 text-base">☺</button>
          {emojiOpen && <div role="menu" aria-label={emojiLabel} className="absolute right-2 top-full z-20 mt-1 grid w-[268px] max-w-[calc(100vw-1rem)] grid-cols-5 gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">{POST_EMOJIS.map((emoji) => <button key={emoji} type="button" role="menuitem" aria-label={emojiNames?.[emoji] || emoji} onMouseDown={(event) => event.preventDefault()} onClick={() => insertEmoji(emoji)} className="min-h-11 min-w-11 rounded-lg border border-slate-200 bg-white text-lg hover:bg-slate-50">{emoji}</button>)}</div>}
        </div>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        className="min-h-28 w-full whitespace-pre-wrap px-3 py-2 text-sm text-slate-900 outline-none empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]"
        onInput={reportChange}
        onKeyDown={(event) => {
          if (event.key === "Escape") { setColourOpen(false); setEmojiOpen(false); return; }
          if ((event.ctrlKey || event.metaKey) && !event.altKey && (event.key.toLowerCase() === "b" || event.key.toLowerCase() === "i")) {
            event.preventDefault();
            apply(event.key.toLowerCase() === "b" ? "bold" : "italic");
          }
        }}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        onPaste={() => window.setTimeout(reportChange, 0)}
      />
    </div>
  );
}
